import get from 'lodash/get'
import size from 'lodash/size'
import { List } from 'immutable'
import { getAuthIsLoggedIn } from '../auth/index'
import { SET_AUTH_LOGIN_SUCCESS } from '../auth/actions'
import { CATEGORIES } from './notificationCategory'
import { createGetNotificationsWorker, createGetMenuNotificationsWorker } from './watchers-helpers'
import * as api from '.'
import {
  NOTIFICATIONS_CREATE_SUBSCRIBABLE,
  NOTIFICATIONS_REMOVE_SUBSCRIBABLE,
  NOTIFICATIONS_GET_SUBSCRIPTIONS,
  NOTIFICATIONS_SET_SUBSCRIPTIONS,
  NOTIFICATIONS_CREATE_SUBSCRIBER,
  NOTIFICATIONS_REMOVE_SUBSCRIBER,
  NOTIFICATIONS_REMOVE_SUBSCRIBER_LIST,
  NOTIFICATIONS_GET_SUBSCRIBABLE,
  NOTIFICATIONS_SET_SUBSCRIBABLE,
  NOTIFICATIONS_UPDATE_RECEIVED,
  NOTIFICATIONS_SET_SUBSCRIBER,
  NOTIFICATIONS_SET_RECEIVED,
  NOTIFICATIONS_SET_RECENT,
  NOTIFICATIONS_SET_RECENT_UNCHANGED,
  NOTIFICATIONS_GET_RECENT,
  NOTIFICATIONS_GET_ALL,
  NOTIFICATIONS_GET_MENU,
} from './actions'

/**
 * One `takeLatest` PER CATEGORY, selected with `.when()` on the action's own category.
 *
 * ⚠️ Not one shared `takeLatest`. That effect cancels its previous worker regardless of payload,
 * so switching to Community while Content was still in flight would cancel the Content fetch —
 * and a cancelled worker never dispatches `SET_ALL`, leaving that tab's `processing` flag true
 * and its spinner up for the rest of the page's life. Two effects means each tab only ever
 * cancels its own in-flight request, which is the behaviour `takeLatest` is wanted for
 * (a double-tapped "Load more" collapses to one) without the cross-tab casualty.
 */
export function getContentNotifications ({ takeLatest }) {
  return takeLatest(
    NOTIFICATIONS_GET_ALL,
    createGetNotificationsWorker(CATEGORIES.CONTENT),
  ).when(({ action }) => get(action, ['payload', 'category']) === CATEGORIES.CONTENT)
}

export function getCommunityNotifications ({ takeLatest }) {
  return takeLatest(
    NOTIFICATIONS_GET_ALL,
    createGetNotificationsWorker(CATEGORIES.COMMUNITY),
  ).when(({ action }) => get(action, ['payload', 'category']) === CATEGORIES.COMMUNITY)
}

/**
 * The bell dropdown's two tabs, one `takeLatest` each — same reasoning as the page's pair above.
 *
 * ⚠️ These do NOT replace `getRecentNotifications` below. That one stays unfiltered because the
 * unread dot is counted from it; these feed the dropdown's Content and Community lists. Three
 * fetches, three jobs (CO-768).
 */
export function getMenuContentNotifications ({ takeLatest }) {
  return takeLatest(
    NOTIFICATIONS_GET_MENU,
    createGetMenuNotificationsWorker(CATEGORIES.CONTENT),
  ).when(({ action }) => get(action, ['payload', 'category']) === CATEGORIES.CONTENT)
}

export function getMenuCommunityNotifications ({ takeLatest }) {
  return takeLatest(
    NOTIFICATIONS_GET_MENU,
    createGetMenuNotificationsWorker(CATEGORIES.COMMUNITY),
  ).when(({ action }) => get(action, ['payload', 'category']) === CATEGORIES.COMMUNITY)
}

export function getRecentNotifications ({ takeLatest }) {
  return takeLatest(
    NOTIFICATIONS_GET_RECENT,
    async ({ state }) => {
      const { auth, user, notifications } = state
      const language = user.getIn(['data', 'language'], ['en'])
      // The ids already in the store — passed to the api so a poll that fetches
      // the same notifications can skip the Brooklyn content fan-out entirely.
      const previousIds = notifications
        .getIn(['recent', 'data'], List())
        .map((n) => n.get('id'))
        .toJS()
      const result = await api.getRecentNotifications({ auth, language, previousIds })

      // Unchanged: clear the transient `processing` flag without rewriting the
      // list (see NOTIFICATIONS_SET_RECENT_UNCHANGED). takeLatest also collapses
      // overlapping polls so a slow fetch can't stack behind a faster one.
      if (result.unchanged) {
        return { type: NOTIFICATIONS_SET_RECENT_UNCHANGED }
      }

      const { data = [] } = result
      return {
        type: NOTIFICATIONS_SET_RECENT,
        payload: data,
      }
    },
  )
}

export function setNotificationsReceived ({ takeMaybe }) {
  return takeMaybe(
    NOTIFICATIONS_UPDATE_RECEIVED,
    ({ state, action }) => {
      const { payload } = action
      const { ids, impressed } = payload
      if (ids && size(ids)) {
        return async () => {
          const { auth } = state
          await api.setNotificationsReceived({ ids, auth })
          return {
            type: NOTIFICATIONS_SET_RECEIVED,
            // ⚠️ `ids` travels through to the reducer, which marks only those rows. It used to
            // receive `impressed` alone and mark every row it held (CO-768).
            payload: { ids, impressed },
          }
        }
      }
      return null
    },
  )
}

export function createSubscriber ({ takeFirst }) {
  return takeFirst(
    NOTIFICATIONS_CREATE_SUBSCRIBER,
    async ({ state, action }) => {
      const { auth } = state
      const { payload: subscription } = action
      const id = subscription.get('id')
      const type = subscription.get('type')
      const contentId = subscription.get('contentId')
      const subscriber = await api.createSubscriber({ id, auth })
      return {
        type: NOTIFICATIONS_SET_SUBSCRIBER,
        payload: {
          subscriber,
          contentId,
          type,
        },
      }
    },
  )
}

export function removeSubscriber ({ takeFirst }) {
  return takeFirst(
    NOTIFICATIONS_REMOVE_SUBSCRIBER,
    async ({ state, action }) => {
      const { auth } = state
      const { payload: subscription } = action
      const id = subscription.get('id')
      const type = subscription.get('type')
      const contentId = subscription.get('contentId')
      await api.removeSubscriber({ id, auth })
      return {
        type: NOTIFICATIONS_SET_SUBSCRIBER,
        payload: {
          subscriber: null,
          contentId,
          type,
        },
      }
    },
  )
}

export function removeSubscriberList ({ takeEvery }) {
  return takeEvery(
    NOTIFICATIONS_REMOVE_SUBSCRIBER_LIST,
    async ({ state, action }) => {
      const { auth } = state
      const { payload: subscriptions = List() } = action
      subscriptions.forEach(async (subscription) => {
        const id = subscription.get('id')
        const type = subscription.get('type')
        const contentId = subscription.get('contentId')
        await api.removeSubscriber({ id, auth })
        return {
          type: NOTIFICATIONS_SET_SUBSCRIBER,
          payload: {
            subscriber: null,
            contentId,
            type,
          },
        }
      })
    },
  )
}

export function getSubscriptions ({ takeEvery }) {
  return takeEvery(
    NOTIFICATIONS_GET_SUBSCRIPTIONS,
    async ({ state, action }) => {
      const { auth, user } = state
      const { payload: inputs = {} } = action
      const { p = 1, pp = 50, version = 'v2' } = inputs
      const language = user.getIn(['data', 'language', 0], 'en')
      const result = await api.getSubscriptions({
        auth, language, p, pp, version,
      })
      const { data = [], total } = result
      return {
        type: NOTIFICATIONS_SET_SUBSCRIPTIONS,
        payload: { data, total },
      }
    },
  )
}

export function getSubscribableEntity ({ takeMaybe }) {
  return takeMaybe(
    NOTIFICATIONS_GET_SUBSCRIBABLE,
    ({ state, action }) => {
      const { payload: inputs } = action
      const { contentId, type } = inputs
      const { notifications, auth } = state
      const isLoggedIn = getAuthIsLoggedIn(auth)
      const key = ['subscribables', type, contentId]

      if (!isLoggedIn || notifications.getIn(key)) {
        return null
      }

      return async () => {
        const entity = await api.getSubscribableEntity({ contentId, type, auth })
        return {
          type: NOTIFICATIONS_SET_SUBSCRIBABLE,
          payload: {
            contentId,
            entity,
            type,
          },
        }
      }
    },
  )
}

export function createSubscribableEntity ({ takeFirst }) {
  return takeFirst(
    NOTIFICATIONS_CREATE_SUBSCRIBABLE,
    async ({ state, action }) => {
      const { auth, user } = state
      const { payload: inputs } = action
      const { contentId, name, type } = inputs
      const payload = await api.createSubscribableEntity({
        contentId, name, type, auth, user,
      })
      return {
        type: NOTIFICATIONS_SET_SUBSCRIBABLE,
        payload,
      }
    },
  )
}

export function removeSubscribableEntity ({ takeFirst }) {
  return takeFirst(
    NOTIFICATIONS_REMOVE_SUBSCRIBABLE,
    async ({ state, action }) => {
      const { auth, user } = state
      const { payload: inputs } = action
      const { contentId, name, type } = inputs
      const payload = await api.removeSubscribableEntity({
        contentId, name, type, auth, user,
      })
      return {
        type: NOTIFICATIONS_SET_SUBSCRIBABLE,
        payload,
      }
    },
  )
}

export function refetchSubscribableEntities ({ after }) {
  return after(SET_AUTH_LOGIN_SUCCESS, async ({ dispatch, state }) => {
    const { auth, notifications } = state
    const subscribables = notifications.get('subscribables', List())
    if (subscribables) {
      let type
      const promiseArr = subscribables.reduce((acc, term) => {
        const keys = term.keySeq().toJS()
        const requests = keys.reduce((reqs, id) => {
          type = term.getIn([id, 'type'])
          const contentId = term.getIn([id, 'contentId'])
          if (contentId) {
            return reqs.concat(api.getSubscribableEntity({
              contentId,
              type,
              auth,
            }))
          }
          return reqs
        }, [])
        return acc.concat(requests)
      }, [])

      // ⚠️ This used to also `dispatch(getNotifications())` to refresh the notifications-center
      // list after a sign-in. That dispatch no longer has a meaning: the list is per-tab now, so
      // a fetch has to name a category, and a watcher cannot know which tab the member is
      // looking at. `NotificationsCenterAll` refetches the tab in view on the auth transition
      // instead, where that is known (CO-768).
      return Promise.all(promiseArr).then((entities) => {
        return entities.map((entity) => {
          const { contentId: entityId } = entity
          const { type: entityType } = entity
          const contentId = entityId
          type = entityType || type
          dispatch({
            type: NOTIFICATIONS_SET_SUBSCRIBABLE,
            payload: {
              contentId,
              entity,
              type,
            },
          })
          return null
        })
      })
    }
    return null
  })
}

export function refetchSubscriptions ({ after }) {
  return after(SET_AUTH_LOGIN_SUCCESS, async ({ dispatch, state }) => {
    const { auth, user } = state
    const language = user.getIn(['data', 'language', 0], 'en')
    const result = await api.getSubscriptions({ auth, language })
    const { data = [] } = result
    dispatch({
      type: NOTIFICATIONS_SET_SUBSCRIPTIONS,
      payload: data,
    })
  })
    .when(({ state }) => {
      const { resolver } = state
      return resolver.getIn(['data', 'path']) === '/notifications/manage'
    })
}
